import { store } from '../../store';
import {
  setToken,
  updateCurrentUser,
  logout as logoutAction,
  startImpersonation,
  stopImpersonation,
} from '../../store/slices/authSlice';
import { env } from '../../config/env';
import axios from 'axios';
import { getAxiosErrorMessage } from '../../utils/getAxiosErrorMessage';
import { baseApi } from '../baseApi';

const apiUrl = env.apiUrl;

async function fetchAndStoreUser(accessToken) {
  const userInfoResponse = await axios.get(`${apiUrl}/auth/user-info`, {
    headers: {
      Authorization: `Bearer ${accessToken}`,
      'Content-Type': 'application/json',
    },
  });

  const userData = userInfoResponse.data;
  store.dispatch(updateCurrentUser(userData));

  return {
    id: userData.sub,
    firstName: userData.given_name,
    lastName: userData.family_name,
    name: `${userData.given_name} ${userData.family_name}`,
    email: userData.email,
    role: userData.role,
    impersonatorSub: userData.impersonator_sub || null,
    impersonatorName: userData.impersonator_name || null,
  };
}

export const authService = {
  async login(email, password) {
    const body = new URLSearchParams({
      grant_type: 'password',
      username: email,
      password,
      scope: 'email profile roles offline_access',
    });

    try {
      const tokenResponse = await axios.post(`${apiUrl}/auth/token`, body.toString(), {
        headers: { 'Content-Type': 'application/x-www-form-urlencoded' },
      });

      const tokenData = tokenResponse.data;

      store.dispatch(
        setToken({
          token: tokenData.access_token,
          refreshToken: tokenData.refresh_token || null,
          expiresAt: tokenData.expires_in
            ? Date.now() + tokenData.expires_in * 1000
            : null,
        })
      );

      const user = await fetchAndStoreUser(tokenData.access_token);

      return {
        success: true,
        user,
        token: tokenData.access_token,
      };
    } catch (err) {
      throw new Error(
        getAxiosErrorMessage(err, 'Login failed. Please check your credentials.')
      );
    }
  },

  loginWithGoogle(returnUrl) {
    // Redirect to the backend challenge endpoint — the backend handles the full OAuth flow
    const url = returnUrl || window.location.href;
    window.location.href = `${apiUrl}/auth/challenge/google?returnUrl=${encodeURIComponent(url)}`;
  },

  loginWithMicrosoft(returnUrl) {
    // Redirect to the backend challenge endpoint — the backend handles the full OAuth flow
    const url = returnUrl || window.location.href;
    window.location.href = `${apiUrl}/auth/challenge/microsoft?returnUrl=${encodeURIComponent(url)}`;
  },

  async exchangeExternalLogin() {
    // Called after the backend callback redirects back with ?external_login=success.
    // The backend set an HttpOnly cookie scoped to /api/auth/token.
    // Exchange it for tokens via the external-login grant type.
    const body = new URLSearchParams({
      grant_type: 'external-login',
      scope: 'email profile roles offline_access',
    });

    try {
      const tokenResponse = await axios.post(`${apiUrl}/auth/token`, body.toString(), {
        headers: { 'Content-Type': 'application/x-www-form-urlencoded' },
        withCredentials: true,
      });

      const tokenData = tokenResponse.data;

      store.dispatch(
        setToken({
          token: tokenData.access_token,
          refreshToken: tokenData.refresh_token || null,
          expiresAt: tokenData.expires_in
            ? Date.now() + tokenData.expires_in * 1000
            : null,
        })
      );

      const user = await fetchAndStoreUser(tokenData.access_token);

      return {
        success: true,
        user,
        token: tokenData.access_token,
      };
    } catch (err) {
      throw new Error(getAxiosErrorMessage(err, 'External login failed.'));
    }
  },

  async exchangeAccountActivation() {
    // Called after ConfirmAccountAsync succeeds. The backend set an HttpOnly cookie
    // scoped to /api/auth/token. Exchange it for tokens via the account-activation grant.
    const body = new URLSearchParams({
      grant_type: 'account-activation',
      scope: 'email profile roles offline_access',
    });

    try {
      const tokenResponse = await axios.post(`${apiUrl}/auth/token`, body.toString(), {
        headers: { 'Content-Type': 'application/x-www-form-urlencoded' },
        withCredentials: true,
      });

      const tokenData = tokenResponse.data;

      store.dispatch(
        setToken({
          token: tokenData.access_token,
          refreshToken: tokenData.refresh_token || null,
          expiresAt: tokenData.expires_in
            ? Date.now() + tokenData.expires_in * 1000
            : null,
        })
      );

      const user = await fetchAndStoreUser(tokenData.access_token);

      return {
        success: true,
        user,
        token: tokenData.access_token,
      };
    } catch (err) {
      throw new Error(getAxiosErrorMessage(err, 'Could not complete sign-in.'));
    }
  },

  /**
   * Admin-only: swap the current session for a token as the target user.
   * Saves the Admin session so exitImpersonation() can restore it.
   * Callers should navigate to getHomeRoute(result.user.role) after success.
   */
  async impersonate(userId) {
    const adminToken = store.getState().auth.token;
    if (!adminToken) {
      throw new Error('You must be signed in as an Admin to impersonate a user.');
    }

    const body = new URLSearchParams({
      grant_type: 'impersonate',
      target_user_id: userId,
      scope: 'email profile roles',
    });

    try {
      const tokenResponse = await axios.post(`${apiUrl}/auth/token`, body.toString(), {
        headers: {
          'Content-Type': 'application/x-www-form-urlencoded',
          Authorization: `Bearer ${adminToken}`,
        },
      });

      const tokenData = tokenResponse.data;

      store.dispatch(startImpersonation());
      store.dispatch(
        setToken({
          token: tokenData.access_token,
          refreshToken: null,
          expiresAt: tokenData.expires_in
            ? Date.now() + tokenData.expires_in * 1000
            : null,
        })
      );

      const user = await fetchAndStoreUser(tokenData.access_token);
      store.dispatch(baseApi.util.resetApiState());

      return {
        success: true,
        user,
        token: tokenData.access_token,
      };
    } catch (err) {
      throw new Error(getAxiosErrorMessage(err, 'Unable to log in as that user.'));
    }
  },

  /**
   * Restores the saved Admin session after impersonation.
   * Returns the restored Admin user (or null if nothing was saved).
   */
  exitImpersonation() {
    const { impersonation } = store.getState().auth;
    if (!impersonation) {
      return null;
    }

    store.dispatch(
      setToken({
        token: impersonation.originalToken,
        refreshToken: impersonation.originalRefreshToken,
        expiresAt: impersonation.originalTokenExpiresAt,
      })
    );

    if (impersonation.originalUser) {
      // Rebuild the user-info-shaped payload expected by updateCurrentUser.
      store.dispatch(
        updateCurrentUser({
          sub: impersonation.originalUser.id,
          given_name: impersonation.originalUser.firstName,
          family_name: impersonation.originalUser.lastName,
          email: impersonation.originalUser.email,
          role: impersonation.originalUser.role,
          broker_company_status: impersonation.originalUser.brokerCompanyStatus || null,
        })
      );
    }

    store.dispatch(stopImpersonation());
    store.dispatch(baseApi.util.resetApiState());

    return store.getState().auth.user;
  },

  logout() {
    // Fire the backend logout (best-effort) then clear local state
    try {
      const token = store.getState().auth.token;
      if (token) {
        axios.put(`${apiUrl}/auth/logout`, null, {
          headers: {
            Authorization: `Bearer ${token}`,
            'Content-Type': 'application/x-www-form-urlencoded',
          },
        }).catch(() => {});
      }
    } catch {
      // Ignore errors during logout
    }
    store.dispatch(logoutAction());
  },

  getCurrentUser() {
    const state = store.getState();
    return state.auth.user || null;
  },

  isAuthenticated() {
    const state = store.getState();
    return state.auth.isAuthenticated;
  },

  isImpersonating() {
    return !!store.getState().auth.impersonation;
  },

  getToken() {
    const state = store.getState();
    return state.auth.token || null;
  }
};
